/**
 * 작성자 : 박승호
 * 날짜 : 2025.12.09
 * 기능 : 이미지 미리보기 컴포넌트
 */

import { useEffect, useMemo, useState } from 'react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import { fileDownloadFn } from '../utils/fileDownloadFn';
import noImage from '../assets/images/common/no_image.png';
import styled from 'styled-components';

const ThumbnailImageRoot = styled.figure<{ size: number; borderRadius: number }>`
  display: flex;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  overflow: hidden;
  border-radius: ${({ borderRadius }) => borderRadius}rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const overlayRender = ({ imageList, currentImageIndex }: { imageList: any; currentImageIndex: number }) => {
  // hooks 사용 금지

  return (
    <div className='photo-view-overlay-total'>
      <span>
        <b>{currentImageIndex + 1}</b> / {imageList.length}
      </span>
    </div>
  );
};

// PhotoToolbar 컴포넌트
const PhotoToolbar = ({
  imageList,
  rotate,
  onRotate,
  onClose,
  currentImageIndex,
}: {
  imageList: any;
  rotate: number;
  onRotate: (angle: number) => void;
  onClose: () => void;
  currentImageIndex: number;
}) => {
  // hooks 사용 금지
  const currentImageFile = imageList[currentImageIndex];

  return (
    <div className='photo-view-options'>
      <div className='photo-view-filename'>{currentImageFile?.fileName ?? ''}</div>
      <div className='photo-view-toolbar-options'>
        {currentImageFile?.fUrl !== '' && (
          <span className='material-symbols-rounded photo-view-toolbar-icon' onClick={(e: any) => fileDownloadFn(e, currentImageFile?.fUrl || '')} role='button' tabIndex={0}>
            download
          </span>
        )}
        <span className='material-symbols-rounded photo-view-toolbar-icon' onClick={(e: any) => onRotate(rotate - 90)} role='button' tabIndex={0}>
          rotate_left
        </span>
        <span className='material-symbols-rounded photo-view-toolbar-icon' onClick={(e: any) => onRotate(rotate + 90)} role='button' tabIndex={0}>
          rotate_right
        </span>
        <span className='material-symbols-rounded photo-view-toolbar-icon' onClick={(e: any) => onClose()} role='button' tabIndex={0}>
          close
        </span>
      </div>
    </div>
  );
};

// 이미지 썸네일 (상위에서 캐시된 previewUrl 우선 사용, 필요 시에만 objectURL 생성)
export const PhotoImage = ({ file, previewUrl, fileName, size = 60, borderRadius = 0.5 }: { file?: any; previewUrl?: string; fileName?: string; size?: number; borderRadius?: number }) => {
  const initialUrl = useMemo(() => previewUrl || file?.fUrl || '', [previewUrl, file?.fUrl]);
  const [resolvedUrl, setResolvedUrl] = useState<string>(initialUrl);

  useEffect(() => {
    let createdUrl: string | null = null;

    // previewUrl/서버 URL이 있으면 추가 생성 없이 바로 사용
    if (previewUrl || file?.fUrl) {
      setResolvedUrl(previewUrl || file?.fUrl || '');
      return () => {};
    }

    // objectURL이 필요한 경우에만 생성
    if (file?.file) {
      createdUrl = URL.createObjectURL(file.file as File);
      setResolvedUrl(createdUrl);
    } else {
      setResolvedUrl('');
    }

    return () => {
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [previewUrl, file]);

  const altText = fileName || file?.fileName || file?.name || 'image';

  return (
    <ThumbnailImageRoot size={size} borderRadius={borderRadius}>
      <PhotoView src={resolvedUrl}>
        <img
          src={resolvedUrl}
          alt={altText}
          role='presentation'
          className='gallery-thumbnail'
          onLoad={(e: any) => {
            e.target.style.backgroundColor = '#f1f1f1';
            return e.target;
          }}
          // 깨진 이미지 대체
          onError={(e: any) => {
            e.target.src = noImage;
            e.target.style.backgroundColor = 'var(--tonal-light)';
            return e.target.src;
          }}
        />
      </PhotoView>
    </ThumbnailImageRoot>
  );
};

// imageList: 이미지 리스트 ( 이미지인 파일만 필터링 필요 )
export const ImagePreview = ({ imageList, children }: { imageList: any; children: React.ReactNode }) => {
  return (
    <PhotoProvider
      easing={(type) => (type === 2 ? '0' : 'cubic-bezier(0.25, 0.8, 0.25, 1)')}
      overlayRender={(overlayProps: any) => {
        const { images, index } = overlayProps;
        return overlayRender({ imageList: images, currentImageIndex: index ?? 0 });
      }}
      toolbarRender={(overlayProps: any) => {
        const { rotate, onRotate, onClose, images, index } = overlayProps;
        return PhotoToolbar({
          imageList,
          rotate,
          onRotate,
          onClose,
          currentImageIndex: index ?? 0,
        });
      }}
      loop={false}
      maskOpacity={0.8}
      speed={(type) => (type === 2 ? 0 : 300)}
      bannerVisible={!false}
      brokenElement={
        <div className='photo-view-broken-image'>
          <span className='material-symbols-rounded'>bid_landscape_disabled</span>
        </div>
      }
    >
      {children}
    </PhotoProvider>
  );
};
